/**
 * Shared API error hierarchy.
 *
 * Every PureID SPA's fetch wrapper throws subclasses of `ApiError`,
 * never raw `Error`. Both the admin and portal clients narrow
 * further (admin adds `StepUpRequiredError` + `ValidationError`;
 * portal is smaller), but both chain back to this base so shared
 * code (e.g. the ceremony runner's typed catch blocks) only has
 * to know about `ApiError`.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    public readonly requestId: string | null,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class AuthError extends ApiError {
  constructor(body: unknown, requestId: string | null, message: string) {
    super(401, body, requestId, message);
    this.name = "AuthError";
  }
}

export class ForbiddenError extends ApiError {
  constructor(body: unknown, requestId: string | null, message: string) {
    super(403, body, requestId, message);
    this.name = "ForbiddenError";
  }
}

export class NotFoundError extends ApiError {
  constructor(body: unknown, requestId: string | null, message: string) {
    super(404, body, requestId, message);
    this.name = "NotFoundError";
  }
}

export class ServerError extends ApiError {
  constructor(
    status: number,
    body: unknown,
    requestId: string | null,
    message: string,
  ) {
    super(status, body, requestId, message);
    this.name = "ServerError";
  }
}

/**
 * StepUpFailedError — the step-up VERIFY endpoint rejected the
 * supplied assertion (wrong passkey picked, signature invalid,
 * user-binding mismatch, ceremony expired, rate-limit hit). The
 * caller's MAIN session is still valid; only the step-up
 * ceremony failed and they should be invited to retry — NOT
 * signed out.
 *
 * This is distinct from AuthError (401 = main session died) so
 * the global SignedOutScreen handler doesn't fire on a
 * recoverable ceremony failure. The step-up surface treats this
 * as an inline error in the existing dialog and lets the user
 * pick another transport / passkey.
 *
 * Why this exists: the backend stepup_handler returns 401 with
 * code "stepup-assertion-rejected" for a failed assertion, which
 * the api-client previously bucketed as AuthError. Fix: detect
 * the stepup-* error codes BEFORE the generic 401/403 fallbacks
 * and lift them out into this distinct type. See api-client
 * builders in apps/admin and apps/portal.
 *
 * Wraps the underlying status (which can be 401, 403, or 429
 * depending on the failure mode) without claiming session-death
 * semantics.
 */
export class StepUpFailedError extends ApiError {
  constructor(
    /** Backend's stable error code: "stepup-assertion-rejected",
     *  "stepup-user-mismatch", "stepup-rate-limited". UI uses
     *  this to surface specific copy ("wrong passkey, try again"
     *  vs "too many attempts, wait a minute"). */
    public readonly code: string,
    status: number,
    body: unknown,
    requestId: string | null,
    message: string,
  ) {
    super(status, body, requestId, message);
    this.name = "StepUpFailedError";
  }
}
